import { useState } from 'react';
import { View } from 'react-native';

import { Button } from '@/components/ui/Button';
import { cn } from '@/components/ui/cn';
import { Text } from '@/components/ui/Text';
import { TextField } from '@/components/ui/TextField';
import { useInFlightGuard } from '@/lib/useInFlightGuard';

/** Append a new immutable note (cannot edit once added). */
export function NoteComposer({
  onSubmit,
  busy,
  className,
}: {
  // May be async; the field is cleared only after it resolves, so a failed
  // submit keeps the user's typed text.
  onSubmit: (body: string) => void | Promise<void>;
  busy?: boolean;
  className?: string;
}) {
  const [body, setBody] = useState('');
  // Notes are immutable and undeletable, so a double-post is permanent; guard the
  // handler and only clear the field once the write succeeds.
  const runExclusive = useInFlightGuard();
  const canSubmit = body.trim().length > 0 && !busy;

  return (
    <View className={cn('ml-9 gap-2', className)}>
      <TextField
        value={body}
        onChangeText={setBody}
        placeholder="Add a note…"
        multiline
        inputClassName="min-h-[64px]"
      />
      <View className="flex-row items-start justify-between">
        <Text variant="caption" className="text-faint pr-2 shrink">
          Attributed to you, can&apos;t be edited once&nbsp;added
        </Text>
        <Button
          size="sm"
          label="Add"
          busy={busy}
          disabled={!canSubmit}
          onPress={() =>
            runExclusive(async () => {
              if (!canSubmit) return;
              try {
                await onSubmit(body.trim());
                setBody(''); // clear only on success — a failure keeps the draft
              } catch {
                // The parent surfaces the error; keep the draft so it isn't lost.
              }
            })
          }
        />
      </View>
    </View>
  );
}
